import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatDobDigits } from "@/lib/dateInput";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const POLL_ATTEMPTS = 5;
const POLL_INTERVAL_MS = 2000;

// The buyer types DOB as MM/DD/YYYY (reverted from the native mobile calendar).
// create-tribute inserts date_of_birth VERBATIM into a Postgres `date` column
// (supabase/functions/create-tribute/index.ts:77,138) and does NO server-side
// validation, so the wire format MUST be YYYY-MM-DD (or null). Convert here and
// reject anything that isn't a real calendar date. Assumes a non-empty input;
// the caller treats an empty field as "no DOB" (-> null) before calling.
const toIsoDate = (raw: string): string | null => {
  const m = raw.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const month = parseInt(m[1], 10);
  const day = parseInt(m[2], 10);
  const year = parseInt(m[3], 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  if (year < 1900 || year > new Date().getFullYear()) return null;
  // Reject impossible dates (e.g. 02/30/2000) via a round-trip.
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
    return null;
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${year}-${pad(month)}-${pad(day)}`;
};

// PAY-FIRST (Project B, C+D): the buyer reaches this page only after paying. Entry
// is gated on an unconsumed tribute_entitlements row — the UX gate below, with the
// create-tribute edge fn as the authoritative consume-once + insert + provision on
// submit. Returning from Stripe (?checkout=success) the gate briefly polls for the
// webhook-written entitlement so a just-paid buyer is never bounced by webhook lag.
const CreateTribute = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  const [gate, setGate] = useState<"checking" | "ready" | "none">("checking");
  const [polling, setPolling] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    date_of_birth: "",
    residence_region: "",
    subject_email: "",
  });
  const [loading, setLoading] = useState(false);
  const [dobError, setDobError] = useState<string>("");
  const [done, setDone] = useState<{ provision: string; subject: string } | null>(null);

  useEffect(() => {
    let cancelled = false;

    // RLS-scoped read ("Buyers read own entitlements"): does the buyer have an
    // unconsumed entitlement to spend?
    const hasUnconsumed = async (): Promise<boolean> => {
      const { data } = await supabase
        .from("tribute_entitlements")
        .select("id")
        .is("consumed_at", null)
        .limit(1)
        .maybeSingle();
      return !!data;
    };

    const run = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!session) {
        navigate("/login", { replace: true });
        return;
      }

      if (await hasUnconsumed()) {
        if (!cancelled) setGate("ready");
        return;
      }
      if (cancelled) return;

      // A normal visit must never hang on the poll — only poll when we just came
      // back from Stripe and likely beat the webhook write.
      if (searchParams.get("checkout") !== "success") {
        setGate("none");
        return;
      }

      setPolling(true);
      for (let i = 0; i < POLL_ATTEMPTS; i++) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        if (cancelled) return;
        if (await hasUnconsumed()) {
          if (!cancelled) {
            setPolling(false);
            setGate("ready");
          }
          return;
        }
      }
      if (!cancelled) {
        setPolling(false);
        setGate("none");
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [navigate, searchParams]);

  const handleSubmit = async () => {
    if (!form.full_name.trim()) {
      toast({ title: "Please enter the subject's name.", variant: "destructive" });
      return;
    }
    const email = form.subject_email.trim();
    if (!email || !EMAIL_RE.test(email)) {
      toast({ title: "Please enter a valid email for the subject.", variant: "destructive" });
      return;
    }

    // DOB is optional. Empty -> null (unchanged). If present, it must be a real
    // MM/DD/YYYY date we can convert to the YYYY-MM-DD the edge fn inserts
    // verbatim; block submit on an invalid/incomplete entry (no server-side
    // validation exists to catch it downstream).
    const dobRaw = form.date_of_birth.trim();
    let dobIso: string | null = null;
    if (dobRaw) {
      dobIso = toIsoDate(dobRaw);
      if (!dobIso) {
        setDobError("Please enter the date of birth as MM/DD/YYYY.");
        return;
      }
    }
    setDobError("");

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-tribute", {
        body: {
          full_name: form.full_name.trim(),
          date_of_birth: dobIso,
          residence_region: form.residence_region.trim() || null,
          subject_email: email,
        },
      });
      if (error) {
        // 402 no_entitlement -> the purchase was missing or already used; surface
        // the gate's "no purchase" panel rather than a raw error toast.
        const ctx = (error as { context?: Response }).context;
        if (ctx && ctx.status === 402) {
          setGate("none");
          setLoading(false);
          return;
        }
        throw error;
      }
      setDone({ provision: data?.provision ?? "pending", subject: form.full_name.trim() });
    } catch (err) {
      const description =
        err instanceof Error ? err.message : "Please try again in a moment.";
      toast({ title: "Could not request the tribute", description, variant: "destructive" });
      setLoading(false);
    }
  };

  if (gate === "checking") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-foreground">
        {polling ? "Confirming your payment..." : "Loading..."}
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md space-y-4 text-center">
          <h1 className="font-display text-2xl font-bold text-foreground">Tribute requested</h1>
          <p className="text-muted-foreground text-sm">
            {done.provision === "sent"
              ? `We've emailed ${done.subject} their private link.`
              : `We'll reach out to ${done.subject} shortly.`}
          </p>
        </div>
      </div>
    );
  }

  if (gate === "none") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md space-y-6 text-center">
          <h1 className="font-display text-2xl font-bold text-foreground">No purchase found</h1>
          <p className="text-muted-foreground text-sm">
            You'll need to purchase a tribute before requesting one.
          </p>
          <Button asChild className="w-full">
            <Link to="/pricing">Purchase a tribute</Link>
          </Button>
        </div>
      </div>
    );
  }

  // gate === "ready"
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="font-display text-2xl font-bold text-foreground">Request a tribute</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Tell us about the person this tribute is for.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="full_name">Their full name</Label>
            <Input
              id="full_name"
              value={form.full_name}
              onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
              required
            />
          </div>
          <div>
            <Label htmlFor="date_of_birth">Date of birth (optional)</Label>
            <Input
              id="date_of_birth"
              type="text"
              inputMode="numeric"
              autoComplete="bday"
              placeholder="MM/DD/YYYY"
              value={form.date_of_birth}
              onChange={(e) => {
                if (dobError) setDobError("");
                setForm((f) => ({ ...f, date_of_birth: formatDobDigits(e.target.value) }));
              }}
            />
            {dobError ? (
              <p className="text-xs text-destructive mt-1">{dobError}</p>
            ) : null}
          </div>
          <div>
            <Label htmlFor="residence_region">State / region of residence (optional)</Label>
            <Input
              id="residence_region"
              value={form.residence_region}
              onChange={(e) => setForm((f) => ({ ...f, residence_region: e.target.value }))}
              placeholder="New York, USA"
            />
          </div>
          <div>
            <Label htmlFor="subject_email">Their email</Label>
            <Input
              id="subject_email"
              type="email"
              value={form.subject_email}
              onChange={(e) => setForm((f) => ({ ...f, subject_email: e.target.value }))}
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              We'll send them a private invitation to record their story.
            </p>
          </div>
          <Button className="w-full" disabled={loading} onClick={handleSubmit}>
            {loading ? "Requesting..." : "Request tribute"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateTribute;
